import { CardControl } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/CardControl';
import { QuickExpand } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickExpand';
import { QuickSave } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickSave';
import { GradientLine } from 'components/detail/minute/GradientLine';
import { ImageValuesPreview } from 'components/detail/minute/ImageValuesPreview';
import { IConfirmActionModal } from 'components/site/modals/ModalWrapper/ModalContent/contents/ConfirmActionModal';
import { EModal } from 'components/site/modals/ModalWrapper/ModalContent/useGetModalProperties';
import { ActionButton as ActionButton2 } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { SidebarSectionHeader } from 'elements/minute/sidebar/sidebarSection/SidebarSection/SidebarSectionHeader';
import { motion } from 'framer-motion';
import { useUploadImage } from 'libs/hooks/io/useUploadImage';
import { conditionalScaleAnimation } from 'libs/styles/animations';
import { EBorders, EHovers } from 'libs/styles/styling';
import { EUploadResolution } from 'libs/utils/logic/operations/fileOperations';
import { useContext, useEffect, useRef, useState } from 'react';
import { CtxApp } from 'session/ctxApp';
import { CtxDetail } from 'session/ctxDetail';
import { CtxModal } from 'session/ctxModal';
import styled, { css } from 'styled-components/macro';

interface Props {
    position: number;
    className?: string;
}

export const EditImageCard = (props: Props): JSX.Element => {
    const ctxDetail = useContext(CtxDetail);
    const card = ctxDetail.updateDetailInput.cards[props.position];
    const [images, setImages] = useState(card.images);
    const { inProgress, uploaded, setFiles } = useUploadImage(
        [EUploadResolution.LARGE, EUploadResolution.BIG],
        ctxDetail.map?.worldMapId
    );
    const isUpdatingIndex = ctxDetail.updatingIndex === props.position;
    const ctxModal = useContext(CtxModal);
    const ctxApp = useContext(CtxApp);
    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (uploaded) {
            console.log('Uploaded', uploaded?.data?.imageUrls);
            if (uploaded?.data?.imageUrls) {
                const imageUrls = uploaded?.data?.imageUrls;
                addImage(imageUrls[EUploadResolution.LARGE], imageUrls[EUploadResolution.BIG]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploaded]);

    //on mounting component, click on the label to start the file selection
    useEffect(() => {
        if (isUpdatingIndex && ctxApp.minuteEditor && inProgress === 0 && images?.length === 0) {
            ref.current?.click();
        }
    }, [isUpdatingIndex]);

    function deleteImage(index: number) {
        ctxModal.setModal({
            type: EModal.CONFIRM,
            data: {
                text: 'Do you want to delete this image?',
                onConfirm: () => {
                    const newImages = [...images];
                    newImages.splice(index, 1);
                    setImages(newImages);
                    ctxDetail.updateCardByPosition(
                        {
                            images: newImages
                        },
                        props.position
                    );
                }
            } as IConfirmActionModal
        });
    }

    function addImage(fullImageUrl: string, imageUrl: string) {
        const newImages = [...images];
        newImages.push({ imageUrl, fullImageUrl });
        setImages(newImages);
        ctxDetail.updateCardByPosition(
            {
                images: newImages
            },
            props.position
        );
    }

    return (
        <motion.div {...conditionalScaleAnimation(true, 1.02, 1.02)}>
            <S_MinuteCard
                className={props.className}
                $isUpdatingIndex={isUpdatingIndex}
                onClick={() => {
                    if (!isUpdatingIndex) {
                        ctxDetail.setUpdatingIndex(props.position);
                    }
                }}
            >
                <S_SidebarSectionHeader
                    $isUpdatingIndex={isUpdatingIndex}
                    icon={{
                        iconName: 'image-polaroid'
                    }}
                    title="Image"
                    highlighted={!!isUpdatingIndex}
                    topBorder
                >
                    {isUpdatingIndex && <QuickSave />}
                    {!isUpdatingIndex && <QuickExpand />}
                </S_SidebarSectionHeader>
                {!isUpdatingIndex && (
                    <S_Content $isUpdatingIndex={isUpdatingIndex}>
                        {images?.length > 0 && (
                            <S_ImagesView
                                images={images.map((image) => {
                                    return image.imageUrl;
                                })}
                            />
                        )}
                    </S_Content>
                )}
                {isUpdatingIndex && (
                    <>
                        <S_Subsection>
                            {images?.length > 0 && (
                                <S_MinuteImages>
                                    {images.map((image, index) => {
                                        return (
                                            <S_MinuteImage key={image.imageUrl}>
                                                <S_ImageValuesPreview
                                                    images={[image.imageUrl]}
                                                    size={100}
                                                />
                                                <S_MinuteIcon
                                                    onClick={() => {
                                                        deleteImage(index);
                                                    }}
                                                    icon={{ iconName: 'trash' }}
                                                />
                                            </S_MinuteImage>
                                        );
                                    })}
                                </S_MinuteImages>
                            )}

                            <S_Upload>
                                <>
                                    <label>
                                        {inProgress === 0 && (
                                            <S_InputFile
                                                multiple
                                                ref={ref}
                                                type="file"
                                                accept="image/x-png,image/png,image/gif,image/jpeg,image/jpg,image/heic,image/heif,image/svg+xml,.heic;capture=camera"
                                                onChange={(e) =>
                                                    setFiles(Array.from(e.target['files']))
                                                }
                                            />
                                        )}
                                        <ActionButton2
                                            text={inProgress === 0 ? 'Upload' : 'Uploading'}
                                            icon={{ iconName: 'upload' }}
                                            disabled={inProgress !== 0}
                                        />
                                    </label>
                                </>
                            </S_Upload>
                        </S_Subsection>

                        <CardControl position={props.position} />
                        <S_GradientLine />
                    </>
                )}
            </S_MinuteCard>
        </motion.div>
    );
};

const S_InputFile = styled.input`
    display: none;
`;

const S_MinuteCard = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
    border-right: 1px solid #d3d3d3;
    border-left: 1px solid #d3d3d3;
    ${(props) => {
        if (props.$isUpdatingIndex) {
            return css`
                border-right: 1px solid #8f3a19;
                border-left: 1px solid #b19052;
            `;
        }
    }}
    ${(props) => {
        if (!props.$isUpdatingIndex) {
            return css`
                &:hover {
                    ${S_SidebarSectionHeader} {
                        box-shadow: ${EHovers.DARK1};
                    }
                    ${S_Content} {
                        box-shadow: ${EHovers.LIGHT4};
                        cursor: pointer;
                    }
                }
            `;
        }
    }}
`;

const S_SidebarSectionHeader = styled(SidebarSectionHeader)<{ $isUpdatingIndex: boolean }>`
    cursor: ${(props) => (props.$isUpdatingIndex ? 'unset' : 'pointer')};
`;

const S_Content = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
`;

const S_ImagesView = styled(ImageValuesPreview)`
    border-bottom: 1px solid ${EBorders.DARK};
`;

const S_ImageValuesPreview = styled(ImageValuesPreview)`
    border: unset;
    padding-top: 0px;
    padding-bottom: 15px;
    padding-left: 0px;
`;

const S_MinuteImages = styled.div`
    display: flex;
    flex-wrap: wrap;
    padding: 15px 15px 0 15px;
    border-bottom: 1px solid ${EBorders.DARK};
    &:hover {
        box-shadow: ${EHovers.LIGHT4};
    }
`;

const S_Subsection = styled.div`
    border-bottom: 1px solid ${EBorders.DARK};
`;

const S_MinuteImage = styled.div`
    display: flex;
    justify-content: flex-start;
    margin: 0 10px 0 0;
`;

const S_GradientLine = styled(GradientLine)`
    margin: 15px 0 0 0;
`;

const S_MinuteIcon = styled(ActionButton2)`
    margin: 10px 5px 0 0px;
`;

const S_Upload = styled.div`
    display: flex;
    justify-content: center;
    margin: 0px 0 0 0;
    padding: 15px 0;
`;
